import { CourseMenuModal } from '~/components/course/sd/CourseComponent'
import { CourseSelection } from '~/components/fusuma/courseSelection/'
import { VisitCondition } from '~/components/fusuma/sd/visitCondition'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import { PlanModal } from '~/components/plan/PlanModal'
import { SeatOnlyModal } from '~/components/plan/SeatOnlyModal'
import { DateTimeSelectionModal } from '~/features/calendar/DateTimeSelectionModal'
import { CouponFusuma } from '~/features/coupon/CouponFusuma'
import { GuideMenus } from '~/features/guide/menus'
import { useMenus } from '~/features/guide/menus/atoms'
import { ReserveSteps } from '~/features/reservationInput/pc/ReserveSteps'
import { ReserveInput } from '~/features/reservationInput/sd'
import { useReserveOverlay } from '~/navigation'
import { ReserveDetailFusuma } from '../detail/ReserveDetailFusuma'
import { useCloseCalendar } from '../reservationInput/shared/hooks'
import { useNavigateWithSelectDateTime } from './hooks/useNavigateWithSelectDateTime'
import type { ReserveStateDatetime } from './state'

/** SD の予約モーダル類をまとめたコンポーネント */
export function SDReserve({ isList }: { isList?: boolean }) {
  return (
    <>
      <CouponFusuma />
      <Menus />
      <DateTimeSelectionModal mode="sd" />
      <CourseSelectFusuma />
      <ReserveDetailFusuma />
      <SeatOnlyModal />
      <CourseMenuModal />
      {/* リストからは /$restaurantId/plan$planId には遷移しないのでここで開かないといけない */}
      {isList && <PlanModal />}
      <CalendarFusuma />
      <ReserveInput />
    </>
  )
}

/** PC の予約モーダル類をまとめたコンポーネント */
export function PcReserve({ isList }: { isList?: boolean }) {
  return (
    <>
      <Menus />
      <DateTimeSelectionModal mode="pcModal" />
      <CalendarModal />
      <ReserveSteps />
      {/*
        Reserve の後の stacking context にしないと、コース選択でコース詳細やプラン詳細が上に重ならない。
        コース選択・予約入力ステップモーダル(コース選択)から、プラン詳細モーダルを表示、予約入力に遷移したときは、
        navigation/store.ts の toPcStack でコース選択・予約入力ステップモーダル(予約入力)だけが残るようにしている
      */}
      <SeatOnlyModal />
      <CourseMenuModal />
      {/* リストからは /$restaurantId/plan$planId には遷移しないのでここで開かないといけない */}
      {isList && <PlanModal />}
      <CouponFusuma title="" detailDirection="horizontal" />
    </>
  )
}

function CourseSelectFusuma() {
  const [context, willBeUnmounted] = useReserveOverlay('COURSE_SELECT')
  if (context === undefined) {
    return null
  }
  return (
    <StackableFusuma title="ご予約内容の選択" willBeUnmounted={willBeUnmounted} headerColor="white">
      <CourseSelection context={context} />
    </StackableFusuma>
  )
}

function CalendarFusuma() {
  const { willBeUnmounted, VisitCondition } = useVisitCondition()
  if (VisitCondition === null) {
    return null
  }

  return (
    <StackableFusuma title="予約人数・日時の確認" willBeUnmounted={willBeUnmounted}>
      <VisitCondition />
    </StackableFusuma>
  )
}

function CalendarModal() {
  const onClose = useCloseCalendar()
  const { willBeUnmounted, VisitCondition } = useVisitCondition()
  if (VisitCondition === null) {
    return null
  }

  return (
    <StackableSlideUpModal
      type="modal"
      animationType="fadeIn"
      title="予約人数、日時の選択"
      willBeUnmounted={willBeUnmounted}
      onClose={onClose}
    >
      <VisitCondition />
    </StackableSlideUpModal>
  )
}

function useVisitCondition() {
  const onClickTime = useNavigateWithSelectDateTime()
  const [context, willBeUnmounted] = useReserveOverlay('CALENDAR')
  if (context === undefined) {
    return { willBeUnmounted, VisitCondition: null }
  }
  const state: ReserveStateDatetime = {
    type: 'datetime',
    restaurantId: context.restaurantId,
    planId: context.planId,
    visitDate: context.visitDate,
    visitorsCount: context.visitorsCount,
  }

  return {
    willBeUnmounted,
    VisitCondition: () => (
      <VisitCondition context={context} state={state} onClickTime={onClickTime} />
    ),
  }
}

function Menus() {
  const [context, willBeUnmounted] = useReserveOverlay('MENUS')
  const menus = useMenus()
  if (context === undefined) {
    return null
  }
  if (!menus) {
    return null
  }
  return <GuideMenus menus={menus} willBeUnmounted={willBeUnmounted} />
}
